// @ts-strict-ignore
import { SERVICE_FAILURE_DEFAULT_VALUES } from './constants';

import type { Action, ServiceObjectInAction, WorkflowServiceConfig } from '~/dashboard/views/workflows/store/engine/types';
import type { DisplayNameOption, FormState } from './ServiceLocalSettingsTab/types';
import type { NodeEditorFormAdapter } from '../useNodeEditorForm';

import { generatePublicId } from 'flow/service/publicIds';
import type { Option } from '@usealloy/component-library';
import type {
  FormattedServiceConfigOption,
  ServiceConfigOption,
} from '~/dashboard/views/settings/service-configuration/types';
import {
  DefaultLevel,
} from '~/dashboard/views/settings/service-configuration/types';
import {
  formatValue,
  getCredentialOwnerOption,
} from '~/dashboard/views/settings/service-configuration/helpers/formatData';
import {
  AlloyOption,
  UnknowOption,
} from '~/dashboard/views/settings/service-configuration/helpers/fixture';
import {
  buildServiceFailureDefaultOption,
  convertServiceOptionToActionOption,
} from './serviceOptionHelpers';
import { getActionName } from 'flow/service/actions';
import type { ServiceOption } from './types';
import { DISPLAY_NAME_OPTION_KEY, WEBHOOK_KEY } from 'flow/service/actionOptions';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import type { WorkflowHistoryContext } from 'flow/store/redux/types';
import { changeDescription } from 'flow/store/redux/types';
import { getNodeEditorFormHtmlElementId } from 'flow/editor/hooks/useWorkflowHistory/helpers';

const addDisplayNameOption = (
  displayNameOption: FormattedServiceConfigOption,
  serviceName: Action['service']['name'],
  formattedServiceConfigOptions: Array<FormattedServiceConfigOption>,
): Array<FormattedServiceConfigOption> => {
  const isDisplayNameSameAsServiceName = displayNameOption.value === serviceName;
  if (!isDisplayNameSameAsServiceName) {
    return [displayNameOption, ...formattedServiceConfigOptions];
  }
  return formattedServiceConfigOptions;
};

export const convertServiceConfigOption = (
  serviceConfig: WorkflowServiceConfig,
  options: Action['options'],
  focusActionCredentialOwner: ServiceObjectInAction['credentialOwner'],
): {
  credentialOwnerOption: Option;
  formattedServiceConfigOptions: FormattedServiceConfigOption[];
} => {
  let credentialOwnerOption = UnknowOption;
  if (!serviceConfig) {
    return {
      credentialOwnerOption,
      formattedServiceConfigOptions: [],
    };
  }
  const serviceConfigOptionsMapping = serviceConfig.serviceConfigOptions?.reduce((acc, option) => {
    acc[option.serviceConfigOptionId] = option;
    return acc;
  }, {} as Record<number, ServiceConfigOption>);

  const customerCredentialOwner = serviceConfig.selectedCustomerOptions?.credentialOwner;
  const optionMap = new Map<number, FormattedServiceConfigOption>();
  // workflow-level values stored in action options (override customer/alloy defaults)
  for (const [key, option] of Object.entries(options)) {
    const optionMetaData = serviceConfigOptionsMapping[option.serviceConfigOptionId];
    if (optionMetaData) {
      const value = formatValue(option, optionMetaData);
      if (option.serviceConfigOptionId) {
        optionMap.set(option.serviceConfigOptionId, {
          serviceConfigOptionId: option.serviceConfigOptionId,
          value,
          isUsingDefault: false,
          defaultLevel: DefaultLevel.NULL,
          key,
          publicId: option.publicId,
          defaultValue: null,
        });

        // If there is a workflow-level owner field, we set the credential owner by using the
        // credential owner in the focusAction.
        if (optionMetaData.isOwner) {
          credentialOwnerOption = getCredentialOwnerOption(focusActionCredentialOwner);
        }
      }
    }
  }

  // customer-level values (override alloy defaults)
  serviceConfig.selectedCustomerOptions?.serviceConfigOptions?.forEach(option => {
    const optionMetaData = serviceConfigOptionsMapping[option.serviceConfigOptionId];
    // augment action option data if set on workflow level
    if (optionMetaData) {
      const value = formatValue(option, optionMetaData);
      if (optionMap.has(option.serviceConfigOptionId)) {
        optionMap.get(option.serviceConfigOptionId).defaultValue = value;
        optionMap.get(option.serviceConfigOptionId).defaultLevel = DefaultLevel.CUSTOMER;
        const mapOption = optionMap.get(option.serviceConfigOptionId);
        mapOption.defaultCredentialOwner = customerCredentialOwner;
      } else {
        // set customer-level default value as the option
        optionMap.set(option.serviceConfigOptionId, {
          serviceConfigOptionId: option.serviceConfigOptionId,
          value,
          defaultValue: value,
          isUsingDefault: true,
          defaultLevel: DefaultLevel.CUSTOMER,
          key: optionMetaData.slug,
          publicId: generatePublicId('actionoption'),
          defaultCredentialOwner: customerCredentialOwner,
        });

        // If the owner field is NOT found at the workflow level but found at the customer level,
        // we overwrite the credential owner to the customer-level credential owner.
        if (optionMetaData.isOwner) {
          credentialOwnerOption = getCredentialOwnerOption(customerCredentialOwner);
        }
      }
    }
  });

  // alloy-level values (lowest precedence, used if no other values are stored)
  serviceConfig.selectedAlloyOptions?.serviceConfigOptions?.forEach(option => {
    const optionMetaData = serviceConfigOptionsMapping[option.serviceConfigOptionId];
    if (optionMetaData) {
      const value = formatValue(option, optionMetaData);

      // Check if there is a customer-level value set.
      if (optionMap.has(option.serviceConfigOptionId)) {
        // If there has customer-level value, we set the Alloy level value as defaultValue.
        if (
          !optionMap.get(option.serviceConfigOptionId).isUsingDefault
          && optionMap.get(option.serviceConfigOptionId).defaultLevel !== DefaultLevel.CUSTOMER
        ) {
          optionMap.get(option.serviceConfigOptionId).defaultValue = value;
          optionMap.get(option.serviceConfigOptionId).defaultLevel = DefaultLevel.ALLOY;
        }
      } else {
        // If there hasn't customer-level value, we use the Alloy value.
        optionMap.set(option.serviceConfigOptionId, {
          serviceConfigOptionId: option.serviceConfigOptionId,
          value,
          defaultValue: value,
          isUsingDefault: true,
          defaultLevel: DefaultLevel.ALLOY,
          key: optionMetaData.slug,
          publicId: generatePublicId('actionoption'),
        });
        // If the owner field is NOT found at the customer level but found at the alloy level,
        // we overwrite the credential owner to the alloy-level credential owner.
        if (optionMetaData.isOwner) {
          credentialOwnerOption = AlloyOption;
        }
      }
    }
  });

  serviceConfig.serviceConfigOptions?.forEach(optionMetaData => {
    const value = formatValue(null, optionMetaData);
    // If the field is required and we don't have value in any level, we place a empty filed.
    if (!optionMap.has(optionMetaData.serviceConfigOptionId) && optionMetaData.isRequired) {
      optionMap.set(optionMetaData.serviceConfigOptionId, {
        serviceConfigOptionId: optionMetaData.serviceConfigOptionId,
        value,
        isUsingDefault: false,
        defaultLevel: DefaultLevel.NULL,
        key: optionMetaData.slug,
        publicId: generatePublicId('actionoption'),
        defaultValue: null,
      });
    }
  });

  return {
    credentialOwnerOption,
    formattedServiceConfigOptions: Array.from(optionMap.values()),
  };
};

const convertFormatedServiceConfigToActionOption = formattedServiceConfigOptions => {
  const newOptions = formattedServiceConfigOptions.reduce((acc, option) => {
    if (!option.isUsingDefault) {
      let value;
      let selectedValues;
      if (Array.isArray(option.value)) {
        value = option.value.map(v => v.label).join();
        selectedValues = option.value.map(v => parseInt(v.value, 10));
      } else if (option.value instanceof Object && option.value.constructor === Object) {
        value = option.value.value;
        selectedValues = null;
      } else {
        value = option.value;
        selectedValues = null;
      }
      acc[option.key] = {
        value,
        jsonValue: null,
        publicId: option.publicId,
        serviceConfigOptionId: option.serviceConfigOptionId,
        selectedValues,
      };
    }
    return acc;
  }, {});
  return newOptions;
};

// form:
export const serviceLocalSettingFormAdapter: NodeEditorFormAdapter<FormState> = {
  history: {
    createContext: ({ action, field }): WorkflowHistoryContext => ({
      operation: Operation.Modify,
      uiEffectContext: createUiEffectContext(changeDescription.serviceLocalSettings, {
        actionId: action.publicId,
        htmlElementId: getNodeEditorFormHtmlElementId('service--configuration', field),
      }),
    }),
  },
  shouldSyncFormFromSource() {
    return true;
  },
  getSourceSyncKey({ action, sourceDefaultFormValues }) {
    const withPersistedPublicId = option => ({
      ...option,
      publicId: action.options[option.key]?.publicId ?? null,
    });

    return {
      ...sourceDefaultFormValues,
      displayNameOption: withPersistedPublicId(sourceDefaultFormValues.displayNameOption),
      // ensures public ids of transient options are stable across form hydration
      formattedServiceConfigOptions: sourceDefaultFormValues.formattedServiceConfigOptions
        .map(withPersistedPublicId),
    };
  },
  getDefaultFormValues(action, workflowProps) {
    const { serviceFailureDefault, service: actionService } = action;
    const service = workflowProps.services[actionService.internalName];
    const { globalServiceConfigOptions } = workflowProps;
    const serviceName = getActionName(action);

    const serviceConfigWithGlobalOptions = {
      ...service.serviceConfig,
      serviceConfigOptions: [
        ...service.serviceConfig.serviceConfigOptions,
        ...globalServiceConfigOptions,
      ],
    };

    const {
      credentialOwnerOption: initCredOwnerOpt,
      formattedServiceConfigOptions: initSvcConfigOpts,
    } = convertServiceConfigOption(
      serviceConfigWithGlobalOptions,
      action.options,
      action.service.credentialOwner,
    );

    const webhookOption = action.options[WEBHOOK_KEY];
    const initialRecurringWebhook: ServiceOption = webhookOption
      ? { ...webhookOption, key: WEBHOOK_KEY, value: String(webhookOption.value) }
      : null;

    const displayNameConfigOption = globalServiceConfigOptions.find(
      opt => opt.slug === DISPLAY_NAME_OPTION_KEY,
    );
    const displayNameOption = initSvcConfigOpts.find(
      option => option.key === DISPLAY_NAME_OPTION_KEY
        && option.serviceConfigOptionId === displayNameConfigOption.serviceConfigOptionId,
    ) as DisplayNameOption;

    const initialDisplayNameOption: DisplayNameOption = displayNameOption ?? {
      serviceConfigOptionId: displayNameConfigOption.serviceConfigOptionId,
      isAdding: false,
      value: getActionName(action),
      defaultValue: getActionName(action),
      isUsingDefault: false,
      defaultLevel: DefaultLevel.ALLOY,
      key: DISPLAY_NAME_OPTION_KEY,
      publicId: generatePublicId('actionoption'),
    } as DisplayNameOption;


    return {
      recurringWorkflow: initialRecurringWebhook,
      serviceFailureDefaultOption: buildServiceFailureDefaultOption(serviceFailureDefault),
      displayNameOption: initialDisplayNameOption,
      credentialOwnerOption: initCredOwnerOpt,
      formattedServiceConfigOptions: initSvcConfigOpts,
      serviceId: service.service_id,
      serviceName,
    };
  },
  mapFormDataToAction(data, sourceAction) {
    const recurringWorkflowData = data.recurringWorkflow
      ? convertServiceOptionToActionOption([data.recurringWorkflow])
      : {};
    const serviceConfigOptions = addDisplayNameOption(
      data.displayNameOption,
      sourceAction.service.name,
      // Display Name is managed separately; exclude its stale duplicate from generic options.
      data.formattedServiceConfigOptions.filter(option => option.key !== DISPLAY_NAME_OPTION_KEY),
    );

    // we want to persist the options that don't have a serviceConfigOptionId
    const optionsWithoutServConfigOption = {};
    for (const [key, option] of Object.entries(sourceAction.options)) {
      if (!option.serviceConfigOptionId) {
        optionsWithoutServConfigOption[key] = option;
      }
    }
    return {
      ...sourceAction,
      options: {
        ...optionsWithoutServConfigOption,
        ...convertFormatedServiceConfigToActionOption(serviceConfigOptions),
        ...recurringWorkflowData,
      },
      serviceFailureDefault: SERVICE_FAILURE_DEFAULT_VALUES[data.serviceFailureDefaultOption.label],
      service: {
        ...sourceAction.service,
        credentialOwner: data.credentialOwnerOption.value,
      },
    };
  },
};
